import { useEffect, useRef } from 'react';
import { useAssistant } from '../components/Assistant/AssistantContext';
import { getTranslations } from '../utils/appLanguage';

const normalizeLang = (lang) => {
  if (!lang) return 'en';
  if (lang === 'ua') return 'uk';
  return lang;
};

const getAssistantErrorText = (lang, key) => {
  const normalizedLang = normalizeLang(lang);
  const t = getTranslations(normalizedLang);
  const fallback = getTranslations('en');
  return t?.assistant?.[key] || fallback?.assistant?.[key] || '';
};

export const useAssistantDialogFeedback = ({
  audioBlob,
  recognizedText,
  matchFailed,
  assistantSuccess,
  dispatch,
  baseLanguage,
  targetLanguage,
}) => {
  const { actions } = useAssistant();

  const prevAudioBlob = useRef(null);
  const prevRecognizedText = useRef(null);
  const prevMatchFailed = useRef(null);
  const prevAssistantSuccess = useRef(null);

  useEffect(() => {
    if (!actions) {
      console.error('AssistantContext actions not provided');
      return;
    }

    const changedRecognition =
      recognizedText &&
      (recognizedText !== prevRecognizedText.current || matchFailed !== prevMatchFailed.current);
    const changedSuccess = assistantSuccess !== prevAssistantSuccess.current;

    if (changedRecognition || changedSuccess) {
      if (assistantSuccess || (!matchFailed && recognizedText)) {
        actions.showSuccess();
        dispatch({ type: 'SET_ASSISTANT_SUCCESS', payload: false });
      } else if (matchFailed && recognizedText) {
        actions.showError();

        const useBaseLang = Math.random() < 0.3;
        const lang = useBaseLang ? baseLanguage : targetLanguage;
        const phraseTemplate = getAssistantErrorText(lang, 'errorDialog');

        setTimeout(() => {
          actions.speakText(phraseTemplate);
        }, 1000);
      }

      prevRecognizedText.current = recognizedText;
      prevMatchFailed.current = matchFailed;
      prevAssistantSuccess.current = assistantSuccess;
    }

    if (!audioBlob && prevAudioBlob.current) {
      prevAudioBlob.current = null;
    }
  }, [audioBlob, recognizedText, matchFailed, assistantSuccess, actions, dispatch, baseLanguage, targetLanguage]);
};

export const useAssistantSingleTranslation = ({
  audioBlob,
  recognizedText,
  matchFailed,
  dispatch,
  baseLanguage,
  targetLanguage,
}) => {
  const { actions } = useAssistant();

  useEffect(() => {
    if (!recognizedText || !actions) return;

    if (!matchFailed && recognizedText) {
      actions.showSuccess();
      dispatch({ type: 'SET_ASSISTANT_SUCCESS', payload: false });
    } else if (matchFailed && recognizedText) {
      actions.showError();

      const useBaseLang = Math.random() < 0.3;
      const lang = useBaseLang ? baseLanguage : targetLanguage;
      const phraseTemplate = getAssistantErrorText(lang, 'errorLessons');

      setTimeout(() => {
        actions.speakText(phraseTemplate);
      }, 1000);
    }
  }, [recognizedText, matchFailed, audioBlob, actions, dispatch, baseLanguage, targetLanguage]);
};
